// Japanese Kana data organized by consonant groups and vowel rows

export type KanaType = 'hiragana' | 'katakana';

export interface KanaChar {
  character: string;
  romaji: string;
  vowel: 'a' | 'i' | 'u' | 'e' | 'o' | 'n';
  group: string;
  type: KanaType;
}

// ============= HIRAGANA =============

// Basic hiragana (gojūon)
export const hiraganaBasic: KanaChar[] = [
  // Vowels (あ行)
  { character: 'あ', romaji: 'a', vowel: 'a', group: 'vowels', type: 'hiragana' },
  { character: 'い', romaji: 'i', vowel: 'i', group: 'vowels', type: 'hiragana' },
  { character: 'う', romaji: 'u', vowel: 'u', group: 'vowels', type: 'hiragana' },
  { character: 'え', romaji: 'e', vowel: 'e', group: 'vowels', type: 'hiragana' },
  { character: 'お', romaji: 'o', vowel: 'o', group: 'vowels', type: 'hiragana' },
  
  // K-row (か行)
  { character: 'か', romaji: 'ka', vowel: 'a', group: 'k', type: 'hiragana' },
  { character: 'き', romaji: 'ki', vowel: 'i', group: 'k', type: 'hiragana' },
  { character: 'く', romaji: 'ku', vowel: 'u', group: 'k', type: 'hiragana' },
  { character: 'け', romaji: 'ke', vowel: 'e', group: 'k', type: 'hiragana' },
  { character: 'こ', romaji: 'ko', vowel: 'o', group: 'k', type: 'hiragana' },
  
  // S-row (さ行)
  { character: 'さ', romaji: 'sa', vowel: 'a', group: 's', type: 'hiragana' },
  { character: 'し', romaji: 'shi', vowel: 'i', group: 's', type: 'hiragana' },
  { character: 'す', romaji: 'su', vowel: 'u', group: 's', type: 'hiragana' },
  { character: 'せ', romaji: 'se', vowel: 'e', group: 's', type: 'hiragana' },
  { character: 'そ', romaji: 'so', vowel: 'o', group: 's', type: 'hiragana' },
  
  // T-row (た行)
  { character: 'た', romaji: 'ta', vowel: 'a', group: 't', type: 'hiragana' },
  { character: 'ち', romaji: 'chi', vowel: 'i', group: 't', type: 'hiragana' },
  { character: 'つ', romaji: 'tsu', vowel: 'u', group: 't', type: 'hiragana' },
  { character: 'て', romaji: 'te', vowel: 'e', group: 't', type: 'hiragana' },
  { character: 'と', romaji: 'to', vowel: 'o', group: 't', type: 'hiragana' },
  
  // N-row (な行)
  { character: 'な', romaji: 'na', vowel: 'a', group: 'n', type: 'hiragana' },
  { character: 'に', romaji: 'ni', vowel: 'i', group: 'n', type: 'hiragana' },
  { character: 'ぬ', romaji: 'nu', vowel: 'u', group: 'n', type: 'hiragana' },
  { character: 'ね', romaji: 'ne', vowel: 'e', group: 'n', type: 'hiragana' },
  { character: 'の', romaji: 'no', vowel: 'o', group: 'n', type: 'hiragana' },
  
  // H-row (は行)
  { character: 'は', romaji: 'ha', vowel: 'a', group: 'h', type: 'hiragana' },
  { character: 'ひ', romaji: 'hi', vowel: 'i', group: 'h', type: 'hiragana' },
  { character: 'ふ', romaji: 'fu', vowel: 'u', group: 'h', type: 'hiragana' },
  { character: 'へ', romaji: 'he', vowel: 'e', group: 'h', type: 'hiragana' },
  { character: 'ほ', romaji: 'ho', vowel: 'o', group: 'h', type: 'hiragana' },
  
  // M-row (ま行)
  { character: 'ま', romaji: 'ma', vowel: 'a', group: 'm', type: 'hiragana' },
  { character: 'み', romaji: 'mi', vowel: 'i', group: 'm', type: 'hiragana' },
  { character: 'む', romaji: 'mu', vowel: 'u', group: 'm', type: 'hiragana' },
  { character: 'め', romaji: 'me', vowel: 'e', group: 'm', type: 'hiragana' },
  { character: 'も', romaji: 'mo', vowel: 'o', group: 'm', type: 'hiragana' },
  
  // Y-row (や行)
  { character: 'や', romaji: 'ya', vowel: 'a', group: 'y', type: 'hiragana' },
  { character: 'ゆ', romaji: 'yu', vowel: 'u', group: 'y', type: 'hiragana' },
  { character: 'よ', romaji: 'yo', vowel: 'o', group: 'y', type: 'hiragana' },
  
  // R-row (ら行)
  { character: 'ら', romaji: 'ra', vowel: 'a', group: 'r', type: 'hiragana' },
  { character: 'り', romaji: 'ri', vowel: 'i', group: 'r', type: 'hiragana' },
  { character: 'る', romaji: 'ru', vowel: 'u', group: 'r', type: 'hiragana' },
  { character: 'れ', romaji: 're', vowel: 'e', group: 'r', type: 'hiragana' },
  { character: 'ろ', romaji: 'ro', vowel: 'o', group: 'r', type: 'hiragana' },
  
  // W-row (わ行)
  { character: 'わ', romaji: 'wa', vowel: 'a', group: 'w', type: 'hiragana' },
  { character: 'を', romaji: 'wo', vowel: 'o', group: 'w', type: 'hiragana' },
  
  // N (ん)
  { character: 'ん', romaji: 'n', vowel: 'n', group: 'special', type: 'hiragana' },
];

// Dakuten (voiced) hiragana
export const hiraganaDakuten: KanaChar[] = [
  // G-row (が行)
  { character: 'が', romaji: 'ga', vowel: 'a', group: 'g', type: 'hiragana' },
  { character: 'ぎ', romaji: 'gi', vowel: 'i', group: 'g', type: 'hiragana' },
  { character: 'ぐ', romaji: 'gu', vowel: 'u', group: 'g', type: 'hiragana' },
  { character: 'げ', romaji: 'ge', vowel: 'e', group: 'g', type: 'hiragana' },
  { character: 'ご', romaji: 'go', vowel: 'o', group: 'g', type: 'hiragana' },
  
  // Z-row (ざ行)
  { character: 'ざ', romaji: 'za', vowel: 'a', group: 'z', type: 'hiragana' },
  { character: 'じ', romaji: 'ji', vowel: 'i', group: 'z', type: 'hiragana' },
  { character: 'ず', romaji: 'zu', vowel: 'u', group: 'z', type: 'hiragana' },
  { character: 'ぜ', romaji: 'ze', vowel: 'e', group: 'z', type: 'hiragana' },
  { character: 'ぞ', romaji: 'zo', vowel: 'o', group: 'z', type: 'hiragana' },
  
  // D-row (だ行)
  { character: 'だ', romaji: 'da', vowel: 'a', group: 'd', type: 'hiragana' },
  { character: 'ぢ', romaji: 'ji', vowel: 'i', group: 'd', type: 'hiragana' },
  { character: 'づ', romaji: 'zu', vowel: 'u', group: 'd', type: 'hiragana' },
  { character: 'で', romaji: 'de', vowel: 'e', group: 'd', type: 'hiragana' },
  { character: 'ど', romaji: 'do', vowel: 'o', group: 'd', type: 'hiragana' },
  
  // B-row (ば行)
  { character: 'ば', romaji: 'ba', vowel: 'a', group: 'b', type: 'hiragana' },
  { character: 'び', romaji: 'bi', vowel: 'i', group: 'b', type: 'hiragana' },
  { character: 'ぶ', romaji: 'bu', vowel: 'u', group: 'b', type: 'hiragana' },
  { character: 'べ', romaji: 'be', vowel: 'e', group: 'b', type: 'hiragana' },
  { character: 'ぼ', romaji: 'bo', vowel: 'o', group: 'b', type: 'hiragana' },
];

// Handakuten (half-voiced) hiragana
export const hiraganaHandakuten: KanaChar[] = [
  // P-row (ぱ行)
  { character: 'ぱ', romaji: 'pa', vowel: 'a', group: 'p', type: 'hiragana' },
  { character: 'ぴ', romaji: 'pi', vowel: 'i', group: 'p', type: 'hiragana' },
  { character: 'ぷ', romaji: 'pu', vowel: 'u', group: 'p', type: 'hiragana' },
  { character: 'ぺ', romaji: 'pe', vowel: 'e', group: 'p', type: 'hiragana' },
  { character: 'ぽ', romaji: 'po', vowel: 'o', group: 'p', type: 'hiragana' },
];

// ============= KATAKANA =============

// Basic katakana (gojūon)
export const katakanaBasic: KanaChar[] = [
  // Vowels (ア行)
  { character: 'ア', romaji: 'a', vowel: 'a', group: 'vowels', type: 'katakana' },
  { character: 'イ', romaji: 'i', vowel: 'i', group: 'vowels', type: 'katakana' },
  { character: 'ウ', romaji: 'u', vowel: 'u', group: 'vowels', type: 'katakana' },
  { character: 'エ', romaji: 'e', vowel: 'e', group: 'vowels', type: 'katakana' },
  { character: 'オ', romaji: 'o', vowel: 'o', group: 'vowels', type: 'katakana' },
  
  // K-row (カ行)
  { character: 'カ', romaji: 'ka', vowel: 'a', group: 'k', type: 'katakana' },
  { character: 'キ', romaji: 'ki', vowel: 'i', group: 'k', type: 'katakana' },
  { character: 'ク', romaji: 'ku', vowel: 'u', group: 'k', type: 'katakana' },
  { character: 'ケ', romaji: 'ke', vowel: 'e', group: 'k', type: 'katakana' },
  { character: 'コ', romaji: 'ko', vowel: 'o', group: 'k', type: 'katakana' },
  
  // S-row (サ行)
  { character: 'サ', romaji: 'sa', vowel: 'a', group: 's', type: 'katakana' },
  { character: 'シ', romaji: 'shi', vowel: 'i', group: 's', type: 'katakana' },
  { character: 'ス', romaji: 'su', vowel: 'u', group: 's', type: 'katakana' },
  { character: 'セ', romaji: 'se', vowel: 'e', group: 's', type: 'katakana' },
  { character: 'ソ', romaji: 'so', vowel: 'o', group: 's', type: 'katakana' },
  
  // T-row (タ行)
  { character: 'タ', romaji: 'ta', vowel: 'a', group: 't', type: 'katakana' },
  { character: 'チ', romaji: 'chi', vowel: 'i', group: 't', type: 'katakana' },
  { character: 'ツ', romaji: 'tsu', vowel: 'u', group: 't', type: 'katakana' },
  { character: 'テ', romaji: 'te', vowel: 'e', group: 't', type: 'katakana' },
  { character: 'ト', romaji: 'to', vowel: 'o', group: 't', type: 'katakana' },
  
  // N-row (ナ行)
  { character: 'ナ', romaji: 'na', vowel: 'a', group: 'n', type: 'katakana' },
  { character: 'ニ', romaji: 'ni', vowel: 'i', group: 'n', type: 'katakana' },
  { character: 'ヌ', romaji: 'nu', vowel: 'u', group: 'n', type: 'katakana' },
  { character: 'ネ', romaji: 'ne', vowel: 'e', group: 'n', type: 'katakana' },
  { character: 'ノ', romaji: 'no', vowel: 'o', group: 'n', type: 'katakana' },
  
  // H-row (ハ行)
  { character: 'ハ', romaji: 'ha', vowel: 'a', group: 'h', type: 'katakana' },
  { character: 'ヒ', romaji: 'hi', vowel: 'i', group: 'h', type: 'katakana' },
  { character: 'フ', romaji: 'fu', vowel: 'u', group: 'h', type: 'katakana' },
  { character: 'ヘ', romaji: 'he', vowel: 'e', group: 'h', type: 'katakana' },
  { character: 'ホ', romaji: 'ho', vowel: 'o', group: 'h', type: 'katakana' },
  
  // M-row (マ行)
  { character: 'マ', romaji: 'ma', vowel: 'a', group: 'm', type: 'katakana' },
  { character: 'ミ', romaji: 'mi', vowel: 'i', group: 'm', type: 'katakana' },
  { character: 'ム', romaji: 'mu', vowel: 'u', group: 'm', type: 'katakana' },
  { character: 'メ', romaji: 'me', vowel: 'e', group: 'm', type: 'katakana' },
  { character: 'モ', romaji: 'mo', vowel: 'o', group: 'm', type: 'katakana' },
  
  // Y-row (ヤ行)
  { character: 'ヤ', romaji: 'ya', vowel: 'a', group: 'y', type: 'katakana' },
  { character: 'ユ', romaji: 'yu', vowel: 'u', group: 'y', type: 'katakana' },
  { character: 'ヨ', romaji: 'yo', vowel: 'o', group: 'y', type: 'katakana' },
  
  // R-row (ラ行)
  { character: 'ラ', romaji: 'ra', vowel: 'a', group: 'r', type: 'katakana' },
  { character: 'リ', romaji: 'ri', vowel: 'i', group: 'r', type: 'katakana' },
  { character: 'ル', romaji: 'ru', vowel: 'u', group: 'r', type: 'katakana' },
  { character: 'レ', romaji: 're', vowel: 'e', group: 'r', type: 'katakana' },
  { character: 'ロ', romaji: 'ro', vowel: 'o', group: 'r', type: 'katakana' },
  
  // W-row (ワ行)
  { character: 'ワ', romaji: 'wa', vowel: 'a', group: 'w', type: 'katakana' },
  { character: 'ヲ', romaji: 'wo', vowel: 'o', group: 'w', type: 'katakana' },
  
  // N (ン)
  { character: 'ン', romaji: 'n', vowel: 'n', group: 'special', type: 'katakana' },
];

// Dakuten (voiced) katakana
export const katakanaDakuten: KanaChar[] = [
  // G-row (ガ行)
  { character: 'ガ', romaji: 'ga', vowel: 'a', group: 'g', type: 'katakana' },
  { character: 'ギ', romaji: 'gi', vowel: 'i', group: 'g', type: 'katakana' },
  { character: 'グ', romaji: 'gu', vowel: 'u', group: 'g', type: 'katakana' },
  { character: 'ゲ', romaji: 'ge', vowel: 'e', group: 'g', type: 'katakana' },
  { character: 'ゴ', romaji: 'go', vowel: 'o', group: 'g', type: 'katakana' },
  
  // Z-row (ザ行)
  { character: 'ザ', romaji: 'za', vowel: 'a', group: 'z', type: 'katakana' },
  { character: 'ジ', romaji: 'ji', vowel: 'i', group: 'z', type: 'katakana' },
  { character: 'ズ', romaji: 'zu', vowel: 'u', group: 'z', type: 'katakana' },
  { character: 'ゼ', romaji: 'ze', vowel: 'e', group: 'z', type: 'katakana' },
  { character: 'ゾ', romaji: 'zo', vowel: 'o', group: 'z', type: 'katakana' },
  
  // D-row (ダ行)
  { character: 'ダ', romaji: 'da', vowel: 'a', group: 'd', type: 'katakana' },
  { character: 'ヂ', romaji: 'ji', vowel: 'i', group: 'd', type: 'katakana' },
  { character: 'ヅ', romaji: 'zu', vowel: 'u', group: 'd', type: 'katakana' },
  { character: 'デ', romaji: 'de', vowel: 'e', group: 'd', type: 'katakana' },
  { character: 'ド', romaji: 'do', vowel: 'o', group: 'd', type: 'katakana' },
  
  // B-row (バ行)
  { character: 'バ', romaji: 'ba', vowel: 'a', group: 'b', type: 'katakana' },
  { character: 'ビ', romaji: 'bi', vowel: 'i', group: 'b', type: 'katakana' },
  { character: 'ブ', romaji: 'bu', vowel: 'u', group: 'b', type: 'katakana' },
  { character: 'ベ', romaji: 'be', vowel: 'e', group: 'b', type: 'katakana' },
  { character: 'ボ', romaji: 'bo', vowel: 'o', group: 'b', type: 'katakana' },
];

// Handakuten (half-voiced) katakana
export const katakanaHandakuten: KanaChar[] = [
  // P-row (パ行)
  { character: 'パ', romaji: 'pa', vowel: 'a', group: 'p', type: 'katakana' },
  { character: 'ピ', romaji: 'pi', vowel: 'i', group: 'p', type: 'katakana' },
  { character: 'プ', romaji: 'pu', vowel: 'u', group: 'p', type: 'katakana' },
  { character: 'ペ', romaji: 'pe', vowel: 'e', group: 'p', type: 'katakana' },
  { character: 'ポ', romaji: 'po', vowel: 'o', group: 'p', type: 'katakana' },
];

// ============= UTILITY FUNCTIONS =============

export const vowelOptions = ['a', 'i', 'u', 'e', 'o', 'n'] as const;
export type VowelType = typeof vowelOptions[number];

export interface KanaFilterOptions {
  includeHiragana?: boolean;
  includeKatakana?: boolean;
  includeDakuten?: boolean;
  includeHandakuten?: boolean;
}

export const getKana = (options: KanaFilterOptions = {}): KanaChar[] => {
  const {
    includeHiragana = true,
    includeKatakana = false,
    includeDakuten = false,
    includeHandakuten = false,
  } = options;

  let chars: KanaChar[] = [];

  if (includeHiragana) {
    chars = [...chars, ...hiraganaBasic];
    if (includeDakuten) chars = [...chars, ...hiraganaDakuten];
    if (includeHandakuten) chars = [...chars, ...hiraganaHandakuten];
  }

  if (includeKatakana) {
    chars = [...chars, ...katakanaBasic];
    if (includeDakuten) chars = [...chars, ...katakanaDakuten];
    if (includeHandakuten) chars = [...chars, ...katakanaHandakuten];
  }

  return chars;
};

export const getKanaByVowel = (
  vowels: VowelType[],
  options: KanaFilterOptions = {}
): KanaChar[] => {
  const allChars = getKana(options);
  return allChars.filter(char => vowels.includes(char.vowel));
};

